import React, { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import { GridColDef } from '@mui/x-data-grid';
import CustomTable from '../../misc/CustomTable';
import DetailsCard from '@common/components/General/DetailsCard';
import { useAppDispatch, useAppSelector } from '../../../store';
import { dateToStringFormat } from '@common/utils/date';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { selectRobotsData } from '../../../store/robots/robots-slice-selectors';
import {
  getDeploymentsByRobotId,
  patchDeploymentByRobotId,
} from '../../../store/robots/robots-actions';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { EditIcon } from '@common/utils/icons/icons';
import { selectIsUserAdminOrHigher } from 'store/general/general-slice-selectors';
import DialogWrapperWithClose from '@common/components/General/DialogWrapperWithClose';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import SearchBar from 'components/ui/SearchBar';
import SearchType from 'models/enums/search';
import SearchResult from 'models/misc/search-result';
import CustomInfoWithLabel from '@common/components/misc/CustomInfoWithLabel';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import { resetRobotsRestState } from 'store/robots/robots-slice';
import getSaveErrorMessages from '@common/functions/general/get-save-error-messages';
import DeploymentTimeline from './deployment-timeline/DeploymentTimeline';
import { patchDeploymentDatesByRobotId } from '../../../store/robots/robots-actions';
import OrganizationRole from '../../../models/enums/organization-roles';
import { selectUser } from 'store/general/general-slice-selectors';
import { DeploymentBlock } from 'utils/deployment-timeline/layout';
import ReassignMissionsDialog, {
  deploymentHasMissionsOutsideRange,
} from './ReassignMissionsDialog';
import { DeploymentDetails } from '../../../models/organization/deployment';
import DeploymentPeriodCell from './DeploymentPeriodCell';

const HistoryPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const robotsData = useAppSelector((state) => selectRobotsData(state));
  const isAdmin = useAppSelector((state) => selectIsUserAdminOrHigher(state));
  const currentUser = useAppSelector((state) => selectUser(state));

  const [editDeploymentId, setEditDeploymentId] = useState<string | null>(null);
  const [selectedOrganization, setSelectedOrganization] = useState<SearchResult | null>(null);
  const [reassignDeploymentId, setReassignDeploymentId] = useState<string | null>(null);

  const canEditDeploymentDates =
    isAdmin && currentUser.data.organization.role === OrganizationRole.MANUFACTURER;
  const robot = robotsData.currentRobot;

  useEffect(() => {
    if (robotsData.rest.getDeploymentsStatus === HttpFetchStatus.Idle && robot?.id) {
      dispatch(getDeploymentsByRobotId(robot.id));
    }
  }, [dispatch, robot?.id, robotsData.rest.getDeploymentsStatus]);

  useEffect(() => {
    if (robotsData.rest.patchDeploymentsStatus === HttpFetchStatus.Success) {
      closeEditDeploymentHandler();
      if (robot?.id) dispatch(getDeploymentsByRobotId(robot.id));
    }
  }, [dispatch, robot?.id, robotsData.rest.patchDeploymentsStatus]);

  const editDeploymentClickHandler = (id: string) => {
    setEditDeploymentId(id);
  };

  const closeEditDeploymentHandler = () => {
    setEditDeploymentId(null);
    setSelectedOrganization(null);
  };

  const selectOrganizationClickHandler = (result: SearchResult) => {
    setSelectedOrganization(result);
  };

  const closeSearchHandler = () => {
    setSelectedOrganization(null);
  };

  const saveClickHandler = () => {
    if (robot != null && editDeployment != null && selectedOrganization != null) {
      dispatch(
        patchDeploymentByRobotId({
          robotId: robot.id,
          data: { id: editDeployment.id, organizationId: selectedOrganization.id },
        }),
      );
    }
  };

  const resetPatchDeploymentStatusHandler = () => {
    dispatch(
      resetRobotsRestState({
        patchDeploymentsStatus: HttpFetchStatus.Idle,
        patchDeploymentsError: null,
      }),
    );
  };

  const resetPatchDeploymentDatesStatusHandler = () => {
    dispatch(
      resetRobotsRestState({
        patchDeploymentDatesStatus: HttpFetchStatus.Idle,
        patchDeploymentDatesError: null,
      }),
    );
  };

  const resetReassignMissionsStatusHandler = () => {
    dispatch(
      resetRobotsRestState({
        reassignMissionsStatus: HttpFetchStatus.Idle,
        reassignMissionsError: null,
      }),
    );
  };

  const deployments = useMemo(
    () => robotsData.currentRobotData?.deployments ?? [],
    [robotsData.currentRobotData?.deployments],
  );
  const reassignDeployment = deployments.find((d) => d.id === reassignDeploymentId);

  const handleSaveDeploymentDates = (updates: DeploymentBlock[]) => {
    if (robot == null) return;
    dispatch(patchDeploymentDatesByRobotId({ robotId: robot.id, updates }));
  };

  const editDeployment = robotsData.currentRobotData?.deployments?.find(
    (d) => d.id === editDeploymentId,
  );

  const periodRows = useMemo(() => {
    const byPeriod = new Map<string, DeploymentDetails[]>();
    deployments.forEach((deployment) => {
      const periodId = deployment.deploymentPeriod?.id;
      if (periodId == null) return;
      const rows = byPeriod.get(periodId) ?? [];
      rows.push(deployment);
      byPeriod.set(periodId, rows);
    });

    const roles = new Map<string, { isPeriodStart: boolean; isPeriodEnd: boolean }>();
    byPeriod.forEach((rows) => {
      const ordered = rows
        .slice()
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
      ordered.forEach((deployment, index) => {
        roles.set(deployment.id, {
          isPeriodStart: index === 0,
          isPeriodEnd: index === ordered.length - 1,
        });
      });
    });

    return { roles, periodCount: byPeriod.size };
  }, [deployments]);

  const columns: GridColDef[] = [
    {
      field: 'deploymentPeriod',
      headerName: t('table-header-deployment-period') as string,
      flex: 1,
      width: 220,
      sortable: false,
      renderCell: (params) => {
        const role = periodRows.roles.get(params.id as string);
        return (
          <DeploymentPeriodCell
            period={(params.row as DeploymentDetails).deploymentPeriod}
            periodCount={periodRows.periodCount}
            isPeriodStart={role?.isPeriodStart ?? false}
            isPeriodEnd={role?.isPeriodEnd ?? false}
          />
        );
      },
    },
    {
      field: 'organizationName',
      headerName: t('table-header-robot-deployed-at') as string,
      flex: 1,
      width: 200,
    },
    {
      field: 'start',
      headerName: t('table-header-robot-deployed-since') as string,
      flex: 1,
      valueFormatter: (params) => dateToStringFormat(DateTime.fromISO(params)),
    },
    {
      field: 'end',
      headerName: t('table-header-robot-deployed-until') as string,
      flex: 1,
      valueFormatter: (params) => (params ? dateToStringFormat(DateTime.fromISO(params)) : '-'),
    },
    {
      field: 'missionsCount',
      headerName: t('table-header-robot-deployed-missions') as string,
      flex: 1,
    },
  ];

  if (isAdmin) {
    columns.push({
      field: 'actions',
      headerName: '',
      renderCell: (params) => {
        const deployment = params.row as DeploymentDetails;
        const showReassign =
          canEditDeploymentDates && deploymentHasMissionsOutsideRange(deployment);
        return (
          <Stack direction="row" width="100%" justifyContent="end">
            {showReassign && (
              <Tooltip title={t('tooltip-reassign-missions-outside-range')}>
                <IconButton
                  color="warning"
                  onClick={() => setReassignDeploymentId(params.id as string)}
                  data-testid={`reassign-missions-button-${params.id}`}
                >
                  <SwapHorizIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              color="success"
              onClick={() => editDeploymentClickHandler(params.id as string)}
            >
              <EditIcon />
            </IconButton>
          </Stack>
        );
      },
    });
  }

  return (
    <React.Fragment>
      <Box p={0.3}>
        <Container maxWidth="lg" sx={{ p: 0 }}>
          <DetailsCard cardTitle={t('title-deployment-history')} enableContentHiding>
            <Stack spacing={1} pb={1}>
              <CustomTable
                autoHeight
                getStatus={robotsData.rest.getStatus && robotsData.rest.getDeploymentsStatus}
                loadingMessage={t('loading-deployments')}
                dataLoaded={robotsData.currentRobotData?.deployments != null}
                failedToLoadMessage={t('error-loading-deployments')}
                data={robotsData.currentRobotData?.deployments ?? []}
                emptyDataMessage={t('info-no-robot-deployments')}
                columns={columns}
                hideFooter
              />
              {canEditDeploymentDates &&
                robotsData.rest.getDeploymentsStatus === HttpFetchStatus.Success && (
                  <DeploymentTimeline
                    deployments={robotsData.currentRobotData?.deployments ?? []}
                    onSave={handleSaveDeploymentDates}
                  />
                )}
            </Stack>
          </DetailsCard>
        </Container>
      </Box>
      {editDeployment != null && (
        <DialogWrapperWithClose
          onClose={closeEditDeploymentHandler}
          open={editDeployment != null}
          title={t('title-edit-deployment')}
          fullWidth
          maxWidth="md"
        >
          <DialogContent>
            <Stack spacing={2}>
              <CustomInfoWithLabel
                label={t('table-header-robot-deployed-at')}
                text={editDeployment.organizationName}
              />
              <CustomInfoWithLabel
                label={t('info-label-deployment-duration')}
                text={`${dateToStringFormat(DateTime.fromISO(editDeployment.start))} - ${
                  editDeployment.end == null
                    ? t('info-label-ongoing')
                    : dateToStringFormat(DateTime.fromISO(editDeployment.end))
                }`}
              />
              <CustomInfoWithLabel
                label={t('table-header-robot-deployed-missions')}
                text={editDeployment.missionsCount.toString()}
              />
              <SearchBar
                topLabel={t('input-label-change-deployment-organization')}
                onItemClick={selectOrganizationClickHandler}
                initialValue={selectedOrganization?.name}
                onClose={closeSearchHandler}
                fields={[SearchType.ORGANIZATION]}
                fullSize
              />
              {selectedOrganization != null && (
                <CustomAlert
                  severity="warning"
                  header={t('info-warning-transferring-deployment-organization')}
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              disabled={selectedOrganization == null}
              onClick={saveClickHandler}
            >
              {t('button-save')}
            </Button>
          </DialogActions>
        </DialogWrapperWithClose>
      )}
      <CustomSnackbar
        severity="success"
        open={robotsData.rest.patchDeploymentsStatus === HttpFetchStatus.Success}
        title={t('success-patching-deployment')}
        onClose={resetPatchDeploymentStatusHandler}
      />
      <CustomSnackbar
        severity="error"
        open={robotsData.rest.patchDeploymentsStatus === HttpFetchStatus.Failed}
        title={t('error-patching-deployment')}
        textMessages={getSaveErrorMessages(robotsData.rest.patchDeploymentsError)}
        onClose={resetPatchDeploymentStatusHandler}
      />
      <CustomSnackbar
        severity="success"
        open={robotsData.rest.patchDeploymentDatesStatus === HttpFetchStatus.Success}
        title={t('success-patching-deployment-dates')}
        onClose={resetPatchDeploymentDatesStatusHandler}
      />
      <CustomSnackbar
        severity="error"
        open={robotsData.rest.patchDeploymentDatesStatus === HttpFetchStatus.Failed}
        title={t('error-patching-deployment-dates')}
        textMessages={getSaveErrorMessages(robotsData.rest.patchDeploymentDatesError)}
        onClose={resetPatchDeploymentDatesStatusHandler}
      />
      {reassignDeployment != null && robot != null && (
        <ReassignMissionsDialog
          open={reassignDeployment != null}
          onClose={() => setReassignDeploymentId(null)}
          robotId={robot.id}
          deployment={reassignDeployment}
          deployments={deployments}
        />
      )}
      <CustomSnackbar
        severity="success"
        open={robotsData.rest.reassignMissionsStatus === HttpFetchStatus.Success}
        title={t('success-reassigning-missions')}
        onClose={resetReassignMissionsStatusHandler}
      />
      <CustomSnackbar
        severity="error"
        open={robotsData.rest.reassignMissionsStatus === HttpFetchStatus.Failed}
        title={t('error-reassigning-missions')}
        textMessages={getSaveErrorMessages(robotsData.rest.reassignMissionsError)}
        onClose={resetReassignMissionsStatusHandler}
      />
    </React.Fragment>
  );
};

export default HistoryPage;
